import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, ShieldCheck } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { STAGE_LABEL } from "@/lib/constants";
import api from "@/lib/api";

function scoreColor(s) { return s >= 80 ? "text-emerald-500" : s >= 60 ? "text-amber-500" : "text-muted-foreground"; }

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => { const params = q ? { q } : {}; const { data } = await api.get("/contacts", { params }); setContacts(data); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  return (
    <PageBody>
      <PageHeader eyebrow="Contacts" title="Your people, one record each"
        subtitle="Every contact with its stage, source and email status. Open one to see the full timeline, emails, replies and deal." />

      <div className="relative w-full sm:w-72 mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email…" className="pl-9" data-testid="contacts-search" />
      </div>

      {contacts.length === 0 ? (
        <EmptyState icon={Users} title="No contacts yet" body="Import or add contacts in the Leads database." />
      ) : (
        <div className="rounded-xl border border-border/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-border/70 bg-card/50 text-xs text-muted-foreground">
                  <th className="text-left font-medium p-3 pl-4">Contact</th>
                  <th className="text-left font-medium p-3">Company</th>
                  <th className="text-left font-medium p-3">Email</th>
                  <th className="text-center font-medium p-3">Fit</th>
                  <th className="text-left font-medium p-3">Stage</th>
                </tr>
              </thead>
              <tbody data-testid="contacts-table">
                {contacts.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/app/contacts/${c.id}`)} data-testid={`contact-row-${c.id}`}
                    className="border-b border-border/40 last:border-0 hover:bg-accent/30 cursor-pointer">
                    <td className="p-3 pl-4">
                      <div className="font-medium flex items-center gap-1.5">{c.name}{c.in_sequence && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="In sequence" />}</div>
                      <div className="text-xs text-muted-foreground">{c.title}</div>
                    </td>
                    <td className="p-3 text-muted-foreground">{c.company}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono text-muted-foreground truncate max-w-[180px]">{c.email || "-"}</span>
                        {c.email_status === "verified" && <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                        {c.email_status === "unknown" && <span className="text-[10px] text-rose-500">no-send</span>}
                      </div>
                    </td>
                    <td className={`p-3 text-center metric ${scoreColor(c.fit_score)}`}>{c.fit_score}</td>
                    <td className="p-3"><Badge variant="outline" className="text-xs capitalize">{STAGE_LABEL[c.stage] || c.stage}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageBody>
  );
}
